import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Trash2, X } from "lucide-react";

interface DeleteConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    projectName: string;
    epicCount: number;
}

export function DeleteConfirmModal({ isOpen, onClose, onConfirm, projectName, epicCount }: DeleteConfirmModalProps) {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[60]"
                        onClick={onClose}
                    />
                    {/* Modal */}
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto border border-red-100 dark:border-red-900/20"
                        >
                            <div className="p-6 text-center">
                                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-500" />
                                </div>

                                <h3 className="text-lg font-bold text-siemens-deep-blue dark:text-white mb-2">
                                    Delete Project?
                                </h3>

                                <p className="text-sm text-siemens-text-soft mb-6">
                                    Are you sure you want to delete <span className="font-bold text-siemens-deep-blue dark:text-white">"{projectName}"</span>?
                                    <br /><br />
                                    {epicCount > 0 ? (
                                        <>
                                            This project contains <span className="font-bold">{epicCount} epics</span>.
                                            <br />
                                            They will be moved to <span className="font-semibold">Legacy Improvements</span>.
                                        </>
                                    ) : (
                                        "This project is empty."
                                    )}
                                </p>

                                <div className="flex gap-3">
                                    <button
                                        onClick={onClose}
                                        className="flex-1 h-10 text-sm font-semibold text-siemens-deep-blue dark:text-white bg-slate-100 dark:bg-slate-800 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={onConfirm}
                                        className="flex-1 h-10 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
